import React, { useState, useEffect } from 'react';
import { reorderPort } from '../composables/fetchAPI';
import toast from 'react-hot-toast';

type LayoutPopupProps = {
  open: boolean;
  portfolios: { portId: string; portName: string }[];
  onClose: () => void;
  onReordered: () => void;
};

const LayoutPopup: React.FC<LayoutPopupProps> = ({ open, portfolios, onClose, onReordered }) => {
  const [orderedPortfolios, setOrderedPortfolios] = useState(portfolios);
  const [draggingIndex, setDraggingIndex] = useState<number | null>(null);

  useEffect(() => {
    if (open) {
      setOrderedPortfolios(portfolios);
    }
  }, [open, portfolios]);

  const handleDragStart = (index: number) => {
    setDraggingIndex(index);
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
  };

  const handleDrop = (index: number) => {
    if (draggingIndex === null) return;

    const draggedItem = orderedPortfolios[draggingIndex];
    const updatedOrder = orderedPortfolios.filter((_, i) => i !== draggingIndex);
    updatedOrder.splice(index, 0, draggedItem); // Insert dragged item at the dropped position
    setOrderedPortfolios(updatedOrder);
    setDraggingIndex(null); // Reset dragging index
  };

  const handleConfirm = async () => {
    try {
      const portIds = orderedPortfolios.map((portfolio) => portfolio.portId); // Extract portfolio IDs in the new order
      // console.log('Reordered Portfolio IDs:', portIds);
      await reorderPort(portIds); // Send reordered IDs to the endpoint
      toast.success('Layout updated successfully!');
      onReordered();
      onClose();
    } catch (error) {
      toast.error('Failed to update layout.');
      console.error(error);
    }
  };

  return open ? (
    <div
      className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 scrollbar-hide"
      onClick={onClose}
    >
      <div
        className=" rounded-xl p-6 w-[600px] h-[100vh] flex flex-col"
        style={{ background: 'var(--component-bg-color)' }}
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-xl font-bold  mb-4"
        style={{ color: 'var(--yellow-text-color)' }}
        >ปรับ Layout</h3>
        <div className="flex gap-6 flex-1">
          {/* รูปแบบการเรียงลำดับ */}
          <div className="flex-1 border border-[#333] rounded-lg p-4 overflow-y-auto max-h-[70vh] scrollbar-hide">
            <h4 className="font-bold mb-2" style={{ color: 'var(--text-color)' }}>ตัวอย่างรูปแบบการเรียงลำดับ</h4>
            <div className="grid grid-cols-2 gap-4">
              {orderedPortfolios.map((portfolio, index) => (
                <div
                  key={portfolio.portId}
                  className="bg-[#23232b] text-white p-4 rounded-lg flex items-center justify-center font-bold"
                >
                  {index + 1}
                </div>
              ))}
            </div>
          </div>

          {/* โปรดเรียงลำดับใหม่ */}
          <div className="flex-1 border border-[#333] rounded-lg p-4 overflow-y-auto max-h-[70vh] scrollbar-hide">
            <h4 className="font-bold mb-2" style={{ color: 'var(--text-color)' }}>โปรดเรียงลำดับใหม่</h4>
            <div className="flex flex-col gap-2">
              {orderedPortfolios.map((portfolio, index) => (
                <div
                  key={portfolio.portId}
                  draggable
                  onDragStart={() => handleDragStart(index)}
                  onDragOver={handleDragOver}
                  onDrop={() => handleDrop(index)}
                  className={`flex items-center gap-4 bg-[#23232b] text-white p-4 rounded-lg cursor-move transition-transform ${
                    draggingIndex === index ? 'scale-105 shadow-lg' : ''
                  }`}
                >
                  <span className="text-gray-400 font-bold flex-shrink-0">{index + 1}</span>
                  <div className="flex-1 bg-[#18181b] p-2 rounded-lg flex items-center">
                    <span>{portfolio.portName}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ยืนยัน */}
        <div className="flex justify-end gap-2 mt-6">
          <button
            className="bg-gray-500 text-white px-4 py-2 rounded font-bold"
            onClick={onClose}
          >
            ยกเลิก
          </button>
          <button
            className="bg-green-500 hover:bg-green-400 text-white px-4 py-2 rounded font-bold"
            onClick={handleConfirm}
          >
            ยืนยัน
          </button>
        </div>
      </div>
    </div>
  ) : null;
};

export default LayoutPopup;